// TH 5 (Anexo 1 del AM 196): licencias de conducción del personal que opera vehículos o maquinaria (DE 255 Art. 51;
// Reglamento a la LOTTTSV Art. 132). M61: job_positions.opera_vehiculos y licencias_aceptadas; worker_licencias (tipo y
// vencimiento por trabajador, sin número ni archivo). Reglas DECIDIDAS POR CLAUDE (por validar con Kevin):
//  - TH 5 aplica solo si algún puesto de la empresa opera vehículos o maquinaria (si no se pudo leer, el motor usa el
//    "tiene vehículos" del Perfil SSO, como antes de M61).
//  - Cumple: cada trabajador ACTIVO (workers.status = 'activo') de esos puestos tiene al menos una licencia de un tipo que
//    su puesto acepta con fecha de vencimiento igual o posterior a hoy en Ecuador (src/lib/fechas.ts).
//  - En proceso: alguno no la tiene, la tiene vencida o de un tipo que su puesto no acepta.
//  - Pendiente: los puestos que operan vehículos no tienen trabajadores activos (sin datos).
//  - Manda el módulo (sync-estados.ts); nunca pisa un "No aplica" manual.
//  - Aviso en la ficha del trabajador si una licencia vence en 30 días o menos (sugerido por StorSafe, no normativo).

import { supabase } from '../supabaseClient';
import { fechaEc, hoyEc, sumarDiasDia } from './fechas';
import { filasSync, type EstadoGuardado, type EstadoSync } from './sync-estados';

const TODOS_LOS_ITEMS_LICENCIAS = ['TH-05'];
export const FUENTE_LICENCIAS = 'licencias_conduccion';
export const DIAS_AVISO_VENCIMIENTO = 30;

export const TIPOS_LICENCIA = ['A', 'A1', 'B', 'C', 'C1', 'D', 'D1', 'E', 'E1', 'F', 'G'] as const;
export type TipoLicencia = typeof TIPOS_LICENCIA[number];

/** Resumen orientativo de cada tipo (StorSafe, no normativo: el alcance exacto lo define la ANT). */
export const DESCRIPCION_LICENCIA: Record<TipoLicencia, string> = {
  A: 'No profesional: motocicletas, tricimotos y cuadrones',
  B: 'No profesional: automóviles y camionetas de hasta 3,5 t',
  F: 'No profesional: vehículos adaptados para personas con discapacidad',
  A1: 'Profesional: mototaxis y tricimotos de transporte comercial',
  C1: 'Profesional: vehículos policiales, militares, municipales y del Estado',
  C: 'Profesional: taxis, camionetas y vehículos de carga de hasta 3,5 t',
  D1: 'Profesional: transporte escolar, institucional y de turismo (hasta 25 pasajeros)',
  D: 'Profesional: buses de transporte de pasajeros',
  E1: 'Profesional: ambulancias y transporte de mercancías o sustancias peligrosas',
  E: 'Profesional: camiones pesados y extrapesados de más de 3,5 t (tráileres, volquetes, tanqueros)',
  G: 'Profesional: maquinaria agrícola, pesada y equipo caminero (montacargas, retroexcavadoras, tractores)',
};


export interface PuestoLicencias { id: string; opera_vehiculos: boolean; licencias_aceptadas: string[] }
export interface TrabajadorLicencias { id: string; full_name?: string; status: string | null; position_id: string | null }
export interface FilaLicencia { id: string; worker_id: string; tipo: string; fecha_vencimiento: string; verificada_por: string | null; verificada_at: string | null }

export interface ResultadoTH05 {
  aplica: boolean;
  estado: EstadoSync;
  conLicencia: number;
  sinLicencia: { id: string; motivo: 'falta' | 'vencida' | 'tipo_no_aceptado' }[];
  total: number;
}

/** ¿La licencia está vigente el día de Ecuador hoyIso (vence ese día o después)? */
export const vigente = (l: Pick<FilaLicencia, 'fecha_vencimiento'>, hoyIso: string) => l.fecha_vencimiento >= hoyIso;

export function resultadoTH05(puestos: PuestoLicencias[], trabajadores: TrabajadorLicencias[], licencias: FilaLicencia[], hoyIso: string = hoyEc()): ResultadoTH05 {
  const operan = new Map(puestos.filter(p => p.opera_vehiculos).map(p => [p.id, new Set(p.licencias_aceptadas)]));
  if (operan.size === 0) return { aplica: false, estado: 'pendiente', conLicencia: 0, sinLicencia: [], total: 0 };
  const deEsos = trabajadores.filter(t => t.status === 'activo' && !!t.position_id && operan.has(t.position_id));
  const sinLicencia: ResultadoTH05['sinLicencia'] = [];
  for (const t of deEsos) {
    const aceptadas = operan.get(t.position_id as string)!;
    const suyas = licencias.filter(l => l.worker_id === t.id);
    const deTipo = suyas.filter(l => aceptadas.has(l.tipo));
    if (deTipo.some(l => vigente(l, hoyIso))) continue;
    sinLicencia.push({ id: t.id, motivo: deTipo.length ? 'vencida' : suyas.length ? 'tipo_no_aceptado' : 'falta' });
  }
  const total = deEsos.length;
  const estado: EstadoSync = total === 0 ? 'pendiente' : sinLicencia.length === 0 ? 'cumple' : 'en_proceso';
  return { aplica: true, estado, conLicencia: total - sinLicencia.length, sinLicencia, total };
}

/** Aviso de la ficha: vencida, o vence en 30 días o menos (sugerido por StorSafe, no normativo). null si no hay aviso. */
export function avisoVencimiento(l: Pick<FilaLicencia, 'fecha_vencimiento'>, hoyIso: string = hoyEc()): string | null {
  if (!vigente(l, hoyIso)) return `Vencida desde el ${fechaEc(l.fecha_vencimiento)}.`;
  if (l.fecha_vencimiento <= sumarDiasDia(hoyIso, DIAS_AVISO_VENCIMIENTO)) {
    return `Vence el ${fechaEc(l.fecha_vencimiento)} (en ${DIAS_AVISO_VENCIMIENTO} días o menos): renuévala a tiempo (aviso sugerido por StorSafe, no normativo).`;
  }
  return null;
}

/** ¿Algún puesto opera vehículos? null si no se pudo leer (sin M61 en la base o sin permiso). */
export async function puestosOperanVehiculos(companyId: string): Promise<boolean | null> {
  if (!companyId) return null;
  const { count, error } = await supabase.from('job_positions').select('id', { head: true, count: 'exact' })
    .eq('company_id', companyId).eq('opera_vehiculos', true);
  if (error) return null;
  return (count ?? 0) > 0;
}

/** Escribe TH-05 si aplica (manda el módulo; nunca pisa un "No aplica"). Sin M61 en la base no toca nada. */
export async function syncLicenciasCompliance(companyId: string, aplica: boolean, hoyIso: string = hoyEc()): Promise<ResultadoTH05 | null> {
  if (!companyId || !aplica) return null;
  const [{ data: ps, error: e1 }, { data: ws, error: e2 }, { data: ls, error: e3 }, { data: existentes, error: e4 }] = await Promise.all([
    supabase.from('job_positions').select('id, opera_vehiculos, licencias_aceptadas').eq('company_id', companyId),
    supabase.from('workers').select('id, status, position_id').eq('company_id', companyId),
    supabase.from('worker_licencias').select('id, worker_id, tipo, fecha_vencimiento, verificada_por, verificada_at').eq('company_id', companyId),
    supabase.from('checklist_item_estados').select('item_id, estado, auto_source').eq('company_id', companyId).in('item_id', TODOS_LOS_ITEMS_LICENCIAS),
  ]);
  if (e1 || e2 || e3 || e4) return null;
  const r = resultadoTH05((ps ?? []) as PuestoLicencias[], (ws ?? []) as TrabajadorLicencias[], (ls ?? []) as FilaLicencia[], hoyIso);
  if (!r.aplica) return r;
  const filas = filasSync(companyId, TODOS_LOS_ITEMS_LICENCIAS, r.estado === 'cumple' ? ['TH-05'] : [], r.total > 0 ? ['TH-05'] : [],
    (existentes ?? []) as EstadoGuardado[], FUENTE_LICENCIAS);
  if (filas.length) {
    const { error } = await supabase.from('checklist_item_estados').upsert(filas, { onConflict: 'company_id,item_id' });
    if (error) console.error('syncLicenciasCompliance', error);
  }
  return r;
}

// ── Ficha del trabajador ──
const mensaje = (e: { code?: string; message: string }, que: string) => (e.code === '42501'
  ? 'Solo el administrador o el responsable pueden cambiar las licencias.'
  : e.code === '23505' ? 'El trabajador ya tiene una licencia de ese tipo: cambia su vencimiento.'
    : `No se pudo ${que}: ${e.message}`);

/** Licencias del trabajador; disponible = false si no se pudo leer (sin M61 en la base): la ficha no se rompe. */
export async function leerLicencias(companyId: string, workerId: string): Promise<{ filas: FilaLicencia[]; disponible: boolean }> {
  try {
    const { data, error } = await supabase.from('worker_licencias').select('id, worker_id, tipo, fecha_vencimiento, verificada_por, verificada_at')
      .eq('company_id', companyId).eq('worker_id', workerId);
    if (error) return { filas: [], disponible: false };
    return { filas: ((data ?? []) as FilaLicencia[]).sort((a, b) => a.tipo.localeCompare(b.tipo)), disponible: true };
  } catch { return { filas: [], disponible: false }; }
}

/** Nombre de quien verificó cada licencia (perfil de la empresa: nombre o, si no tiene, correo). Un id que la RLS de
 *  profiles no deja leer, o cuyo perfil ya no existe, no aparece en el mapa. */
export async function leerNombresVerificadores(ids: string[]): Promise<Record<string, string>> {
  const unicos = Array.from(new Set(ids));
  if (unicos.length === 0) return {};
  try {
    const { data, error } = await supabase.from('profiles').select('id, full_name, email').in('id', unicos);
    if (error) return {};
    const m: Record<string, string> = {};
    for (const p of (data ?? []) as { id: string; full_name: string | null; email: string | null }[]) {
      const nombre = p.full_name || p.email;
      if (nombre) m[p.id] = nombre;
    }
    return m;
  } catch { return {}; }
}

/** Texto de la verificación en la ficha. */
export function textoVerificacion(l: Pick<FilaLicencia, 'verificada_por' | 'verificada_at'>, nombres: Record<string, string>): string {
  if (!l.verificada_at) return 'Sin verificar';
  const nombre = l.verificada_por ? nombres[l.verificada_por] : undefined;
  return nombre ? `Verificada por ${nombre} el ${fechaEc(l.verificada_at)}` : `Verificada el ${fechaEc(l.verificada_at)} (usuario no disponible)`;
}

/** Si el puesto opera vehículos y qué tipos acepta; null si no hay puesto o no se pudo leer. */
export async function leerPuestoLicencias(positionId: string): Promise<{ opera: boolean; aceptadas: string[] } | null> {
  try {
    const { data, error } = await supabase.from('job_positions').select('opera_vehiculos, licencias_aceptadas').eq('id', positionId);
    const p = (data as { opera_vehiculos?: boolean; licencias_aceptadas?: string[] | null }[] | null)?.[0];
    if (error || !p) return null;
    return { opera: !!p.opera_vehiculos, aceptadas: p.licencias_aceptadas ?? [] };
  } catch { return null; }
}

export async function agregarLicencia(companyId: string, workerId: string, tipo: TipoLicencia, fechaVencimiento: string): Promise<void> {
  const { error } = await supabase.from('worker_licencias').insert({ company_id: companyId, worker_id: workerId, tipo, fecha_vencimiento: fechaVencimiento });
  if (error) throw new Error(mensaje(error, 'registrar la licencia'));
}

export async function cambiarVencimiento(id: string, fechaVencimiento: string): Promise<void> {
  const { error } = await supabase.from('worker_licencias').update({ fecha_vencimiento: fechaVencimiento }).eq('id', id);
  if (error) throw new Error(mensaje(error, 'cambiar el vencimiento'));
}

/** Verificar: la base pone quién (el usuario de la sesión) y cuándo (M61); aquí solo se marca. */
export async function verificarLicencia(id: string, usuarioId: string): Promise<void> {
  const { error } = await supabase.from('worker_licencias').update({ verificada_por: usuarioId }).eq('id', id);
  if (error) throw new Error(mensaje(error, 'verificar la licencia'));
}

export async function borrarLicencia(id: string): Promise<void> {
  const { error } = await supabase.from('worker_licencias').delete().eq('id', id);
  if (error) throw new Error(mensaje(error, 'borrar la licencia'));
}
